/**
 * Auth provider для Refine.
 *
 * Поток:
 * 1. login() → редиректим на бэкенд /api/v1/auth/login (он редиректит на NC OIDC).
 * 2. Бэкенд после успешного OIDC-callback редиректит на `<return_to>#token=<JWT>`.
 *    Мы парсим hash в bootstrapTokenFromHash() при старте App.
 * 3. Токен сохраняем в localStorage как 'finance.token'.
 * 4. check() — проверяет наличие токена и его срок.
 * 5. logout() — очищаем токен (опционально вызываем NC logout endpoint).
 * 6. getIdentity() — дёргаем /api/v1/auth/me с Bearer-токеном.
 */
import type { AuthProvider } from "@refinedev/core";

const TOKEN_KEY = "finance.token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * При старте приложения проверяем window.location.hash на #token=...
 * (бэкенд после OIDC callback редиректит сюда). Сохраняем и чистим hash.
 */
export function bootstrapTokenFromHash(): void {
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  if (!hash.startsWith("#token=")) return;
  const token = hash.substring("#token=".length);
  if (token) {
    setToken(token);
    // Чистим hash чтобы JWT не светился в URL
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/** Декодировать JWT без проверки подписи (только для UX — настоящая проверка на backend). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  const exp = (payload?.exp as number | undefined) ?? 0;
  if (!exp) return true;
  return Date.now() / 1000 >= exp;
}

export const authProvider: AuthProvider = {
  /**
   * Вызывается когда юзер кликает Login (или у Refine onUnauthenticated).
   * Редиректим на бэкенд, который перенаправит на NC OIDC.
   */
  login: async () => {
    // return_to — текущий URL с фрагментом маршрута, чтобы вернуться после auth
    const returnTo = window.location.pathname + window.location.search || "/";
    const url = `/api/v1/auth/login?return_to=${encodeURIComponent(returnTo)}`;
    window.location.href = url;
    return { success: true };
  },

  logout: async () => {
    clearToken();
    return { success: true, redirectTo: "/" };
  },

  check: async () => {
    const token = getToken();
    if (!token || isTokenExpired(token)) {
      clearToken();
      // Запоминаем куда юзер хотел попасть до /login,
      // чтобы вернуть его сюда после OIDC callback
      const here = window.location.pathname + window.location.search;
      if (here && here !== "/login") {
        sessionStorage.setItem("finance.return_to", here);
      }
      return {
        authenticated: false,
        redirectTo: "/login",
        logout: true,
      };
    }
    return { authenticated: true };
  },

  getPermissions: async () => {
    const token = getToken();
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    return (payload?.role as string | undefined) ?? null;
  },

  getIdentity: async () => {
    const token = getToken();
    if (!token) return null;
    try {
      const resp = await fetch("/api/v1/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        if (resp.status === 401) clearToken();
        return null;
      }
      return await resp.json();
    } catch {
      return null;
    }
  },

  onError: async (error) => {
    // 401 → выкидываем на login
    if ((error as { status?: number })?.status === 401) {
      clearToken();
      return { logout: true, redirectTo: "/login" };
    }
    return {};
  },
};
